import React, { useCallback, useMemo } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { compactInr, fontForColumn, inr, pct, PROJECT_ABBREVIATIONS, PROJECT_INITIALS, projectColor, THEME } from '../utils/trackerConfig';
import { textFits, useFontsReady } from '../utils/textFit';
import { useElementSize } from '../../departmentIncomeTracker/utils/layoutHooks';
import TarPill from './TarPill';
import DrillValue from './DrillValue';

// below this project-column width (about a 440px container) the summary is a list of project blocks instead of a table
const LIST_COLUMN_BELOW_PX = 62;
const baseCellSx = { py: 0.7, fontSize: 13, borderRight: `1px solid ${THEME.grid}`, borderBottom: `1px solid ${THEME.grid}` };
// dense = the compact page: tighter rows, same content
const denseCellSx = { ...baseCellSx, py: 0.25, fontSize: 12, lineHeight: 1.3 };

/**
 * The sheet's top block (B7:G14): a row per measure, a column per project - plus a Total column
 * the sheet doesn't have but every reader ends up adding. Each project column carries its own
 * colour (the same colour follows that project into the tabs and the CRO rows). Rows that are
 * empty for every project (Sales TAR / Fee TAR while only a total target exists) are left out.
 * Figures are centred in their columns, and every one opens the rows behind it (`onDrill`).
 */
export default function SummaryTable({ projects, showOgd, onPickProject, activeProject, onDrill, dense = false }) {
  // The table works out how wide a project column is and picks the biggest type that fits; if even then the full amounts would not fit they
  // are written in lakh / crore (and the TAR loses its decimal), so nothing is cut or drawn over its neighbour on a small screen.
  const [boxRef, , boxW] = useElementSize();
  useFontsReady();
  const columnPx = boxW === null ? 120 : (boxW * 0.76) / (projects.length + (projects.length > 1 ? 1 : 0));
  const fontSize = Math.min(dense ? 12 : 13, fontForColumn(columnPx));
  const fullTexts = [
    ...projects.flatMap((p) => [p.total.totalRevenue, p.summary.ogdRevenue]),
    projects.reduce((t, p) => t + (Number(p.total.totalRevenue) || 0), 0),
    projects.reduce((t, p) => t + (Number(p.summary.ogdRevenue) || 0), 0),
  ].map((v) => inr(v));
  // On a phone six number columns cannot each hold a whole amount: below this column width the table becomes one block per project with its
  // figures in a 2- or 3-across grid, so every amount is shown in full (nothing is shortened or cut).
  const listMode = boxW !== null && columnPx < LIST_COLUMN_BELOW_PX;
  const perRow = listMode && textFits(fullTexts, (boxW - 16) / 3 - 6, 13, true, 22) ? 3 : 2;
  const compact = !listMode && !textFits(fullTexts, columnPx, fontSize, true, 14);
  // the narrowest columns cannot even hold 6 characters (12,481): thousands are written 12.5K there
  const thousands = compact && !textFits(['88,888'], columnPx, fontSize, true, 14);
  // the TAR keeps its decimal (603.4%) whenever the longest one fits its pill, else it is written 603%
  const totalTar = projects.reduce((t, p) => t + (Number(p.total.target) || 0), 0) > 0 ? (projects.reduce((t, p) => t + (Number(p.total.totalRevenue) || 0), 0) / projects.reduce((t, p) => t + (Number(p.total.target) || 0), 0)) * 100 : null;
  const tarTexts = [...projects.map((p) => p.summary.overallTar), totalTar].filter((v) => v !== null && v !== undefined).map((v) => pct(v, 1));
  const tarDecimals = listMode || textFits(tarTexts, columnPx, fontSize, true, 28) ? 1 : 0;
  const num = useCallback((v) => (Number(v) ? (compact ? compactInr(v, thousands) : inr(v)) : '–'), [compact, thousands]);
  const shortNames = columnPx < 84; // "Foundation" needs about 80px at this size
  const cellSx = { ...(dense ? denseCellSx : baseCellSx), fontSize };
  const rows = useMemo(() => {
    const sum = (fn) => projects.reduce((t, p) => t + (Number(fn(p)) || 0), 0);
    const totalRevenue = sum((p) => p.total.totalRevenue);
    const totalTarget = sum((p) => p.total.target);
    const ogdAdm = sum((p) => p.summary.ogdAdmissions);
    const ogdRev = sum((p) => p.summary.ogdRevenue);
    const crdTotal = sum((p) => p.summary.crdAdmissions);

    // drill(p) / totalDrill = the request a click opens, or null when there is nothing behind the number
    const money = (label, value, scope) => (Number(value) ? { metric: 'totalRevenue', title: label, scope } : null);
    const all = [
      {
        label: 'Total Revenue (₹)',
        strong: true,
        get: (p) => num(p.total.totalRevenue),
        total: num(totalRevenue),
        drill: (p) => money(`${p.label} — Total Revenue`, p.total.totalRevenue, { projectKey: p.key }),
        totalDrill: money('All projects — Total Revenue', totalRevenue, {}),
      },
      {
        label: 'Overall TAR',
        get: (p) => <TarPill dense decimals={tarDecimals} value={p.summary.overallTar} />,
        total: <TarPill dense decimals={tarDecimals} value={totalTarget > 0 ? (totalRevenue / totalTarget) * 100 : null} />,
        values: (p) => p.summary.overallTar,
        drill: (p) =>
          p.summary.overallTar === null || p.summary.overallTar === undefined
            ? null
            : { metric: 'tar', title: `${p.label} — TAR`, formula: { kind: 'tar', revenue: p.total.totalRevenue, target: p.total.target, tar: p.summary.overallTar } },
        totalDrill:
          totalTarget > 0
            ? { metric: 'tar', title: 'All projects — TAR', formula: { kind: 'tar', revenue: totalRevenue, target: totalTarget, tar: (totalRevenue / totalTarget) * 100 } }
            : null,
      },
      { label: 'Sales TAR', get: (p) => <TarPill value={p.summary.salesTar} />, total: <TarPill value={null} />, values: (p) => p.summary.salesTar },
      { label: 'Fee TAR', get: (p) => <TarPill value={p.summary.feeTar} />, total: <TarPill value={null} />, values: (p) => p.summary.feeTar },
      {
        label: 'CRD Admissions',
        get: (p) => num(p.summary.crdAdmissions),
        total: num(crdTotal),
        // distinct admission numbers per project - the same count the cell shows
        drill: (p) => (p.summary.crdAdmissions ? { metric: 'sales', title: `${p.label} — CRD Admissions`, scope: { projectKey: p.key, groupBy: 'admn' } } : null),
        totalDrill: crdTotal ? { metric: 'sales', title: 'All projects — CRD Admissions', scope: { groupBy: 'admnProject' } } : null,
      },
      ...(showOgd
        ? [
            {
              label: 'OGD Admissions',
              get: (p) => num(p.summary.ogdAdmissions),
              total: num(ogdAdm),
              drill: (p) => (p.summary.ogdAdmissions ? { metric: 'sales', title: `${p.label} — OGD Admissions`, scope: { ogd: true, projectKey: p.key, groupBy: 'admn' } } : null),
              totalDrill: ogdAdm ? { metric: 'sales', title: 'All projects — OGD Admissions', scope: { ogd: true, groupBy: 'admnProject' } } : null,
            },
            {
              label: 'OGD Revenue (₹)',
              get: (p) => num(p.summary.ogdRevenue),
              total: num(ogdRev),
              drill: (p) => (p.summary.ogdRevenue ? { metric: 'totalRevenue', title: `${p.label} — OGD Revenue`, scope: { ogd: true, projectKey: p.key } } : null),
              totalDrill: ogdRev ? { metric: 'totalRevenue', title: 'All projects — OGD Revenue', scope: { ogd: true } } : null,
            },
            {
              label: 'OGD ARPU (₹)',
              get: (p) => num(p.summary.ogdArpu),
              total: ogdAdm > 0 ? num(ogdRev / ogdAdm) : '–',
              drill: (p) =>
                p.summary.ogdAdmissions > 0
                  ? { metric: 'tar', title: `${p.label} — OGD ARPU`, formula: { kind: 'arpu', revenue: p.summary.ogdRevenue, admissions: p.summary.ogdAdmissions } }
                  : null,
              totalDrill: ogdAdm > 0 ? { metric: 'tar', title: 'All projects — OGD ARPU', formula: { kind: 'arpu', revenue: ogdRev, admissions: ogdAdm } } : null,
            },
          ]
        : []),
    ];
    return all.filter((r) => !r.values || projects.some((p) => r.values(p) !== null && r.values(p) !== undefined));
  }, [projects, showOgd, num, tarDecimals]);

  const wrap = (node, request) => (onDrill && request ? <DrillValue onClick={() => onDrill(request)}>{node}</DrillValue> : node);

  if (listMode) {
    const blocks = [
      ...projects.map((p) => ({ key: p.key, title: p.label, color: projectColor(p.key).main, pick: () => onPickProject && onPickProject(p.key), get: (r) => ({ node: r.get(p), request: r.drill && r.drill(p) }) })),
      ...(projects.length > 1 ? [{ key: '__total', title: 'Total', color: THEME.primary, pick: null, get: (r) => ({ node: r.total, request: r.totalDrill }) }] : []),
    ];
    return (
      <Box ref={boxRef} sx={{ backgroundColor: 'white', border: `1px solid ${THEME.grid}`, borderRadius: '12px', boxShadow: '0 1px 6px rgba(15,23,42,0.10)', overflow: 'hidden' }}>
        {blocks.map((b) => (
          <Box key={b.key} sx={{ borderBottom: `1px solid ${THEME.grid}` }}>
            <Box
              onClick={b.pick || undefined}
              sx={{ px: 1, py: 0.5, color: '#fff', fontWeight: 800, fontSize: 13, backgroundColor: b.color, cursor: b.pick && onPickProject ? 'pointer' : 'default' }}
            >
              {b.title}
              {activeProject === b.key && ' ✓'}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))`, gap: '6px 2px', px: 0.5, py: 0.75, backgroundColor: b.key === '__total' ? THEME.total : '#fff' }}>
              {rows.map((r) => {
                const { node, request } = b.get(r);
                return (
                  <Box key={r.label} sx={{ textAlign: 'center', minWidth: 0 }}>
                    <Typography component="div" sx={{ fontSize: 10.5, lineHeight: 1.2, color: '#64748B', fontWeight: 600 }}>
                      {r.label.replace(' (₹)', '')}
                    </Typography>
                    <Box sx={{ fontSize: 13, fontWeight: r.strong ? 800 : 700, color: r.strong ? projectColor(b.key).text : THEME.primaryDark, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {wrap(node, request)}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <TableContainer ref={boxRef} sx={{ backgroundColor: 'white', border: `1px solid ${THEME.grid}`, borderRadius: '12px', boxShadow: '0 1px 6px rgba(15,23,42,0.10)', overflowX: 'hidden' }}>
      {/* fixed layout: the label column and every project column share the width, so the table never scrolls sideways */}
      <Table size="small" sx={{ fontVariantNumeric: 'tabular-nums', tableLayout: 'fixed', width: '100%', '& th, & td': { px: 0.75, overflowWrap: 'anywhere' } }}>
        <colgroup>
          <col style={{ width: '24%' }} />
          {projects.map((p) => (
            <col key={p.key} />
          ))}
          {projects.length > 1 && <col />}
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...cellSx, backgroundColor: THEME.primarySoft }} />
            {projects.map((p) => {
              const pc = projectColor(p.key);
              const active = activeProject === p.key;
              return (
                <TableCell
                  key={p.key}
                  align="center"
                  onClick={() => onPickProject && onPickProject(p.key)}
                  sx={{
                    ...cellSx,
                    fontWeight: 800,
                    color: '#fff',
                    backgroundColor: pc.main,
                    cursor: onPickProject ? 'pointer' : 'default',
                    boxShadow: active ? 'inset 0 -4px 0 #fff' : 'none',
                    '&:hover': onPickProject ? { filter: 'brightness(0.93)' } : {},
                  }}
                >
                  {columnPx < 50 ? PROJECT_INITIALS[p.key] || p.label : shortNames ? PROJECT_ABBREVIATIONS[p.key] || p.label : p.label}
                </TableCell>
              );
            })}
            {projects.length > 1 && (
              <TableCell align="center" sx={{ ...cellSx, fontWeight: 800, color: '#fff', backgroundColor: THEME.primary }}>
                Total
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.label} hover sx={{ '&:hover': { backgroundColor: `${THEME.rowHover} !important` } }}>
              <TableCell sx={{ ...cellSx, fontWeight: 700, color: THEME.primaryDark, backgroundColor: THEME.primarySoft }}>{r.label}</TableCell>
              {projects.map((p) => (
                <TableCell
                  key={p.key}
                  align="center"
                  sx={{ ...cellSx, fontWeight: r.strong ? 800 : 600, color: r.strong ? projectColor(p.key).text : '#334155', backgroundColor: '#fff' }}
                >
                  {wrap(r.get(p), r.drill && r.drill(p))}
                </TableCell>
              ))}
              {projects.length > 1 && (
                <TableCell align="center" sx={{ ...cellSx, fontWeight: 800, color: THEME.primaryDark, backgroundColor: THEME.total }}>
                  {wrap(r.total, r.totalDrill)}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {compact && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, py: 0.4, lineHeight: 1.2 }}>
          Amounts in ₹ · L = lakh (1,00,000) · Cr = crore (1,00,00,000){thousands ? ' · K = thousand' : ''}
        </Typography>
      )}
    </TableContainer>
  );
}
